/**
 * Calculates a point on the CDF of the exponential distribution.
 * CDF(x) = 1 - e^(-(x - loc) / scale)
 * @param x The value at which to evaluate the CDF.
 * @param loc The location parameter (shift along the x-axis).
 * @param scale The scale parameter (stretching of the distribution).
 * @returns The cumulative probability up to x.
 */
const exponentialCDF = (x: number, loc: number, scale: number): number => {
  if (x <= loc) return 0
  return 1 - Math.exp(-(x - loc) / scale)
}

/**
 * Generates points for a (potentially truncated) exponential distribution.
 * @param loc The location parameter (mu), which shifts the distribution left or right.
 * @param scale The scale parameter (beta), which controls the spread. scale = 1 / lambda.
 * @param lbound Optional lower bound for truncating the distribution.
 * @param ubound Optional upper bound for truncating the distribution.
 * @param numPoints The number of points to generate for the curve.
 * @returns An array of {x, y} points representing the distribution's PDF.
 */
export const exponentialSample = (
  loc: number,
  scale: number,
  lbound?: number,
  ubound?: number,
  numPoints = 100,
) => {
  const points = []

  // --- 1. Define the range for x-axis sampling ---
  // Start from the effective lower bound (either loc, or the user-defined lbound if greater)
  const sampleStart = lbound !== undefined ? Math.max(loc, lbound) : loc
  // End at a practical limit, or the user-defined ubound if it's smaller
  const practicalMax = loc + 12 * scale // After 12 scales, probability is negligible
  const sampleEnd = ubound !== undefined ? Math.min(practicalMax, ubound) : practicalMax

  if (sampleStart >= sampleEnd) {
    // This can happen if lbound >= ubound or lbound is beyond the practical max.
    // Return a flat line or an empty array as there's no range to sample.
    return [
      { x: sampleStart, y: 0 },
      { x: sampleEnd, y: 0 },
    ]
  }

  const step = (sampleEnd - sampleStart) / (numPoints - 1)

  // --- 2. Calculate the normalization factor for truncation ---
  // This is the total probability mass within the [lbound, ubound] interval.
  // We'll divide the standard PDF by this factor to "re-distribute" the probability.

  // Determine the bounds for CDF calculation. Use -Infinity and +Infinity if not provided.
  const cdfLowerBound = lbound ?? -Infinity
  const cdfUpperBound = ubound ?? Infinity

  // The total probability in the truncated range is CDF(upper) - CDF(lower).
  const totalProbabilityInBounds =
    exponentialCDF(cdfUpperBound, loc, scale) - exponentialCDF(cdfLowerBound, loc, scale)

  // The normalization factor is 1 divided by this probability.
  // If the probability is zero (e.g., an impossible range), avoid division by zero.
  const normalizationFactor = totalProbabilityInBounds > 1e-9 ? 1.0 / totalProbabilityInBounds : 1.0

  // --- 3. Generate the points ---
  for (let i = 0; i < numPoints; i++) {
    const x = sampleStart + i * step

    // Calculate standard PDF value, but only if x is in the distribution's natural domain (x >= loc)
    // And also within the user-defined bounds if they exist.
    const isInBounds =
      x >= loc && (lbound === undefined || x >= lbound) && (ubound === undefined || x <= ubound)

    let y = 0
    if (isInBounds) {
      // Standard PDF formula: (1/scale) * e^(-(x - loc) / scale)
      const standardPdf = (1 / scale) * Math.exp(-(x - loc) / scale)

      // Adjust the y-value by the normalization factor.
      // If the distribution isn't truncated, normalizationFactor will be 1.
      // If it is truncated, this factor will be > 1, increasing the y-value.
      y = standardPdf * normalizationFactor
    }

    points.push({ x, y })
  }

  // Ensure the first and last points are exactly at the bounds if specified
  // This improves plotting accuracy.
  if (points.length > 0) {
    const firstPoint = points[0]
    if (lbound !== undefined && firstPoint && firstPoint.x > lbound) {
      points.unshift({ x: lbound, y: 0 })
    }
    const lastPoint = points[points.length - 1]
    if (ubound !== undefined && lastPoint && lastPoint.x < ubound) {
      points.push({ x: ubound, y: 0 })
    }
  }

  return points
}
